import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {GetSignableDocumentListResponseBody} from '@dms/api/core';

import {Nullish} from 'shared/web';

import {BASE_PATH_SEPARATOR} from '../constants/BASE_PATH_SEPARATOR';
import {DefaultSignatoriesFormData} from '../types/DefaultSignatoriesFormData';
import {buildSignatoriesFormValues} from './buildSignatoriesFormValues';

type SignatoriesFormValues = ReturnType<typeof buildSignatoriesFormValues>;

/**
 * Applies only the non-empty default signatory fields onto every document/signatory
 * form field; where a default field is missing, the document's current value is kept.
 * Backs the lenient "Apply to all documents" action, which skips validation.
 */
export const mergeSignatoriesFormValues = (
  documents: GetSignableDocumentListResponseBody[] | Nullish,
  defaults: DefaultSignatoriesFormData,
  currentValues: SignatoriesFormValues
) =>
  (documents ?? []).reduce<SignatoriesFormValues>((formValues, document) => {
    const documentValues = document.metadata.reduce((values, signatory) => {
      const fieldKey = `${document.sourceFileId}${BASE_PATH_SEPARATOR}${signatory.id}`;

      return {
        ...values,
        [fieldKey]: mergeSignatoryFormValue(
          currentValues[fieldKey],
          defaults[signatory.signatoryPerson]
        ),
      };
    }, {});

    return {...formValues, ...documentValues};
  }, {});

const mergeSignatoryFormValue = (
  current: SignatoriesFormValues[string],
  defaults: DefaultSignatoriesFormData[string] | undefined
): SignatoriesFormValues[string] => {
  if (isNil(defaults)) {
    return current;
  }

  return {
    id: isNotNilOrEmpty(defaults.id) ? defaults.id : current?.id,
    name: isNotNilOrEmpty(defaults.name) ? (defaults.name ?? undefined) : current?.name,
    email: isNotNilOrEmpty(defaults.email) ? (defaults.email ?? undefined) : current?.email,
    phoneNumber: isNotNilOrEmpty(defaults.phoneNumber?.number)
      ? defaults.phoneNumber
      : current?.phoneNumber,
  };
};
